import * as React from "react"
import fetch from "isomorphic-fetch"
import Client from "shopify-buy"
import axios from 'axios';
import { deferIdle } from '@utils/scriptLoader';

import cartCreateMutation from '../graphql/CartCreate.graphql';
import RetrieveCart from '../graphql/RetrieveCart.graphql';
import AddToCart from '../graphql/AddToCart.graphql';
import UpdateLineItem from '../graphql/UpdateLineItem.graphql';
import RemoveLineItem from '../graphql/RemoveLineItem.graphql';
import UpdateBuyersIdentityMutation from '../graphql/UpdateBuyersIdentity.graphql';
import AwinUpdateCartAttributesMutation from '../graphql/AwinUpdateCartAttributes.graphql';

import { countryCurrencies } from '@staticdata/currencies'
import { buildAddedToCartPayload, buildStartedCheckoutPayload, klaviyoTrack } from '@utils/klaviyo'

const client = Client.buildClient({
        domain: process.env.GATSBY_MYSHOPIFY_URL,
        storefrontAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
    },
    fetch
)

const defaultValues = {
    cart: [],
    isOpen: false,
    loading: false,
    onOpen: () => {},
    onClose: () => {},
    addVariantToCart: () => {},
    buyNow: () => {},
    removeLineItem: () => {},
    updateLineItem: () => {},
    client,
    checkout: {
        lines: {
            edges: []
        }
    },
}



export const StoreContext = React.createContext(defaultValues)

const isBrowser = typeof window !== `undefined`
const localStorageKey = `headless_channel_cart_id`
const session_id = `session_id`

export const StoreProvider = ({ children }) => {
    const [checkout, setCheckout] = React.useState(defaultValues.checkout);
    const [isOpen, setIsOpen] = React.useState(defaultValues.isOpen);
    const [loading, setLoading] = React.useState(defaultValues.loading);

    const [didJustAddToCart, setDidJustAddToCart] = React.useState(false)
    const [conversionRate, setConversionRate] = React.useState(0)

    const pendingQtyRef = React.useRef(new Map())
    const qtyTimerRef = React.useRef(null)
    const commitPromiseRef = React.useRef(null)

    const [currencySelected, setCurrencySelected] = React.useState(() => {
        if (typeof window === "undefined") {
            const defaultCode = process.env.GATSBY_STORE !== "PH" ? "US" : "PH";
            return countryCurrencies.find(item => item.countryCode === defaultCode) ?? {};
        }

        const country = localStorage.getItem("userCountry");
        if (country) {
            return JSON.parse(country);
        }

        const defaultCode = process.env.GATSBY_STORE !== "PH" ? "US" : "PH";
        return countryCurrencies.find(item => item.countryCode === defaultCode) ?? {};
    });
    const awc = isBrowser ? localStorage.getItem("awin_awc") : null;

    //Freebie for palacio
    //const freebieItemId = "gid://shopify/ProductVariant/42277814304791";
    //const freeAmika = process.env.GATSBY_STORE !== "PH" ? "gid://shopify/ProductVariant/44723945504791" : "gid://shopify/ProductVariant/45862117245124";
    const freeBratzBox = process.env.GATSBY_STORE !== "PH" ? "gid://shopify/ProductVariant/44655764930583" : "gid://shopify/ProductVariant/45814925885636";
    const freeTropicalGlow = process.env.GATSBY_STORE !== "PH" ? "gid://shopify/ProductVariant/45115047247895" : "gid://shopify/ProductVariant/45115047247895";
    
    
    const gwp = process.env.GATSBY_STORE !== "PH" ? false : false;
    //const isGlobal = process.env.GATSBY_STORE !== "PH" ? true : false;
    const totalAmountCondition = process.env.GATSBY_STORE !== "PH" ? 99 : 5800;

    React.useEffect(() => {
        const getCurrencies = async () => {
            const response = await fetch(`https://hexarate.paikama.co/api/rates/${currencySelected?.currencyCode}/USD/latest`);

            const data = await response.json();
            if(data?.status_code === 200) {
                setConversionRate(data?.data?.mid)
            }
        }

        deferIdle(getCurrencies);

    }, []);

    const setCheckoutItem = (checkout) => {
        if (isBrowser) {
            localStorage.setItem(localStorageKey, checkout.id)
        }
        setCheckout(checkout)
    }

    React.useEffect(() => {
        //redirect to PH site
        if(process.env.GATSBY_STORE !== "PH" && currencySelected?.countryCode === "PH") {
            window.location.href = "https://blackboughswim.ph";
            return
        }

        const updateCountry = async () => {
            const country = localStorage.getItem("userCountry");
            const countryObj = country ? JSON.parse(country) : null;

            if (JSON.stringify(countryObj) !== JSON.stringify(currencySelected)) {
                localStorage.setItem(
                    "userCountry",
                    JSON.stringify(currencySelected)
                );

                await updateBuyersIdentity();

                initializeCheckout()

                if (countryObj !== null) {
                    window.location.reload()
                }

            }
        }

        updateCountry();

    }, [currencySelected]);

    React.useEffect(() => {
         const updateCartForAwin = async () => {
            if(awc !== null && checkout?.id) {
                await updateCartAttributes();
            }
        }

        updateCartForAwin();
        
    }, [awc, checkout?.id]);
    
    React.useEffect(() => {
        initializeCheckout()
    }, []);

    const initializeCheckout = async () => {
        const existingCheckoutID = isBrowser ? localStorage.getItem(localStorageKey) : null;

        if (existingCheckoutID && existingCheckoutID !== `null`) {
            try {
                const existingCheckout = await getCart(existingCheckoutID)
            
                if (!existingCheckout.completedAt) {
                    setCheckoutItem(existingCheckout)
                    return
                }
            } catch {
                localStorage.setItem(localStorageKey, null)
            }
        }

        const newCheckout = await createCart();
        if(newCheckout !== null) {
            setCheckoutItem(newCheckout)
        }
    }

    const updateCartAttributes = async (
        cartId = checkout?.id,
        attributes = {
            key: "awin_awc",
            value: awc
        }
    ) => {
        const variables = { cartId, attributes };
        const query = AwinUpdateCartAttributesMutation.loc.source.body;
        
        try {
            const response = await axios.post(`https://${process.env.GATSBY_MYSHOPIFY_URL}/api/2025-07/graphql.json`, 
                {
                    query,
                    variables,
                }, 
                {
                    headers: {
                        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.errors) {
                //console.error('GraphQL Errors:', response.data.errors);
                return null;
            }
            
        
            const { cartAttributesUpdate } = response.data.data;
        
            if (cartAttributesUpdate.userErrors.length > 0) {
                //console.error('User Errors:', cartCreate.userErrors);
                return null;
            }
        
            //console.log('Cart Created:', cartCreate.cart);
            return cartAttributesUpdate.cart;
        } catch {
            //console.error("Error creating cart:", error.response?.data || error.message);
            return null;
        }
    }

    const updateBuyersIdentity = async (
        cartId = checkout?.id,
        buyerIdentity = {
            countryCode: currencySelected?.countryCode
        }
    ) => {
        const variables = { cartId, buyerIdentity };
        const query = UpdateBuyersIdentityMutation.loc.source.body;
        
        try {
            const response = await axios.post(`https://${process.env.GATSBY_MYSHOPIFY_URL}/api/2025-07/graphql.json`, 
                {
                    query,
                    variables,
                }, 
                {
                    headers: {
                        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.errors) {
                //console.error('GraphQL Errors:', response.data.errors);
                return null;
            }
        
            const { updateBuyersIden } = response.data.data;
        
            if (updateBuyersIden.userErrors.length > 0) {
                //console.error('User Errors:', cartCreate.userErrors);
                return null;
            }
        
            //console.log('Cart Created:', cartCreate.cart);
            return updateBuyersIden.cart;
        } catch {
            //console.error("Error creating cart:", error.response?.data || error.message);
            return null;
        }
    }
    
    const createCart = async (cartInput = {
        buyerIdentity: {
            countryCode: currencySelected?.countryCode
        }
    }) => {

        const variables = { input: cartInput };
        const query = cartCreateMutation.loc.source.body;
        
        try {
            const response = await axios.post(`https://${process.env.GATSBY_MYSHOPIFY_URL}/api/2025-07/graphql.json`, 
                {
                    query,
                    variables,
                }, 
                {
                    headers: {
                        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
                        'Content-Type': 'application/json',
                    },
                }
            );
        
            if (response.data.errors) {
                //console.error('GraphQL Errors:', response.data.errors);
                return null;
            }
        
            const { cartCreate } = response.data.data;
        
            if (cartCreate.userErrors.length > 0) {
                //console.error('User Errors:', cartCreate.userErrors);
                return null;
            }
        
            //console.log('Cart Created:', cartCreate.cart);
            return cartCreate.cart;
        } catch {
            //console.error("Error creating cart:", error.response?.data || error.message);
            return null;
        }
    }

    const getCart = async (cartId) => {
        const query = RetrieveCart.loc.source.body;

        try {
            const response = await axios.post(`https://${process.env.GATSBY_MYSHOPIFY_URL}/api/2025-07/graphql.json`, 
                {
                    query,
                    variables: { id: cartId },
                },
                {
                    headers: {
                        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
                        'Content-Type': 'application/json',
                    },
                }
            );
      
            return response?.data?.data?.cart;
        } catch (error) {
            //console.error('Error fetching cart:', error);
            return { error: error.message };
        }
    }

    const buyNow = async (variantId, quantity) => {
        setLoading(true)
        const cartId = checkout?.id;

        const variables = {
            cartId,
            lines: [
              {
                merchandiseId: variantId,
                quantity: quantity
              }
            ]
          };
        const query = AddToCart.loc.source.body;
        
        try {
            const response = await axios.post(`https://${process.env.GATSBY_MYSHOPIFY_URL}/api/2025-07/graphql.json`, 
                {
                    query,
                    variables,
                }, 
                {
                    headers: {
                        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
                        'Content-Type': 'application/json',
                    },
                }
            );
        
            if (response.data.errors) {
                //console.error('GraphQL Errors:', response.data.errors);
                return null;
            }
        
            const { cartLinesAdd } = response.data.data;
        
            if (cartLinesAdd.userErrors.length > 0) {
                //console.error('User Errors:', cartLinesAdd.userErrors);
                return null;
            }

			try {
				const addedLine = cartLinesAdd?.cart?.lines?.edges?.find(
					(edge) => edge?.node?.merchandise?.id === variantId
				)?.node
				if (addedLine) {
					klaviyoTrack('Added to Cart', buildAddedToCartPayload({ addedLine, cart: cartLinesAdd.cart }))
				}
			} catch {
				// ignore tracking errors
			}

			try {
				klaviyoTrack('Started Checkout', buildStartedCheckoutPayload({ cart: cartLinesAdd.cart }))
			} catch {
				// ignore tracking errors
			}
            
            const buyNowUrl = cartLinesAdd?.cart?.checkoutUrl;
            if (buyNowUrl) {
                if(process.env.GATSBY_STORE !== "PH") {
                    window.location.href = buyNowUrl.replace("blackboughswim.com", "checkout.blackboughswim.com");
                } else {
                    window.location.href = buyNowUrl.replace("blackboughswim.ph", "checkout.blackboughswim.ph");
                }
            }
        } catch {
            //console.error("Error creating cart:", error.response?.data || error.message);
            return null;
        }

        // Redirect to the Shopify checkout URL
        
    };


    const onOpen = () => {
        setIsOpen(true);
    }

    const onClose = () => {
        setIsOpen(false);
    }

    const getCartPromoTotals = (cartLines, promo) => {
        return cartLines.reduce(
            (acc, line) => {
                const metafieldValue = line.node.merchandise.product.metafield?.value ?? "";
                const quantity = Number(line.node.quantity) || 0;
                const price = Number(line.node.cost?.amountPerQuantity?.amount) || 0;
                const lineTotal = quantity * price;

                if (metafieldValue === promo) {
                    acc.hasPromo = true;
                    acc.promoTotal += lineTotal;
                    acc.countPromo += quantity;
                    acc.nonPromoTotal += lineTotal;
                } else {
                    acc.nonPromoTotal += lineTotal;
                }

                acc.count += quantity;
                acc.total += lineTotal;

                return acc;
            },
            {
                hasPromo: false,
                promoTotal: 0,
                nonPromoTotal: 0,
                countPromo: 0,
                count: 0,
                total: 0,
            }
        );
    };
    

    const addVariantToCart = async (variantId, quantity) => {
        setLoading(true)
        const cartId = checkout?.id;

        const variables = {
            cartId,
            lines: [
              {
                merchandiseId: variantId,
                quantity: quantity
              }
            ]
          };
        const query = AddToCart.loc.source.body;
        
        try {
            const response = await axios.post(`https://${process.env.GATSBY_MYSHOPIFY_URL}/api/2025-07/graphql.json`, 
                {
                    query,
                    variables,
                }, 
                {
                    headers: {
                        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
                        'Content-Type': 'application/json',
                    },
                }
            );
        
            if (response.data.errors) {
                console.error('GraphQL Errors:', response.data.errors);
                setLoading(false)
                return null;
            }

            const { cartLinesAdd } = response.data.data;

            if (cartLinesAdd.userErrors.length > 0) {
                console.error('User Errors:', cartLinesAdd.userErrors);
                setLoading(false)
                return null;
            }

			try {
				const addedLine = cartLinesAdd?.cart?.lines?.edges?.find(
					(edge) => edge?.node?.merchandise?.id === variantId
				)?.node
				if (addedLine) {
					klaviyoTrack('Added to Cart', buildAddedToCartPayload({ addedLine, cart: cartLinesAdd.cart }))
				}
			} catch {
				// ignore tracking errors
			}
            
            /*
            const cartLines = cartLinesAdd?.cart?.lines?.edges;
            
            //Bratz GWP
            const keepSafePromo = getCartPromoTotals(cartLines, "bratz");
            
            //const keepSakesTotalOver = currencySelected.currencyCode !== "USD" && currencySelected.currencyCode !== "PHP" ? keepSafePromo?.total * conversionRate : keepSafePromo?.total;
            const bratzPromo = currencySelected.currencyCode !== "USD" && currencySelected.currencyCode !== "PHP" ? keepSafePromo?.promoTotal * conversionRate : keepSafePromo?.promoTotal;

            //Free Bratz
            if(gwp && bratzPromo >= totalAmountCondition && !cartLinesAdd?.cart?.lines?.edges?.some(line => line.node.merchandise.id === freeBratzBox)) {
                const getFreebie = await addFreebie(freeBratzBox);

                if(getFreebie !== null) {
                    setCheckout(getFreebie.cart)
                    setLoading(false)
                    setDidJustAddToCart(true)
                    setIsOpen(true)
                    setTimeout(() => setDidJustAddToCart(false), 3000)

                    return getFreebie.cart;
                }
            }

            //Free sticker pack
            
            const TropicalStorem = getCartPromoTotals(cartLines, "tropical-glow");

            if(TropicalStorem.hasPromo && currencySelected?.currencyCode === "USD" && !cartLinesAdd?.cart?.lines?.edges?.some(line => line.node.merchandise.id === freeTropicalGlow)) {
                const getFreebie = await addFreebie(freeTropicalGlow);

                if(getFreebie !== null) {
                    setCheckout(getFreebie.cart)
                    setLoading(false)
                    setDidJustAddToCart(true)
                    setIsOpen(true)
                    setTimeout(() => setDidJustAddToCart(false), 3000)

                    return getFreebie.cart;
                }
            }
            
            */

            
            setCheckout(cartLinesAdd.cart)
            setLoading(false)
            setDidJustAddToCart(true)
            setIsOpen(true)
            setTimeout(() => setDidJustAddToCart(false), 3000)

            return cartLinesAdd.cart;
        } catch (error) {
            console.error("Error creating cart:", error.response?.data || error.message);
            setLoading(false)
            return null;
        }
    }

    const addVariantsToCartBulk = async (items = []) => {
        setLoading(true);
        
        const cartId = checkout?.id;
      
        if (!items.length || !cartId) return null;
      
        const variables = {
          cartId,
          lines: items.map(item => ({
            merchandiseId: item.id,
            quantity: 1
          }))
        };
      
        const query = AddToCart.loc.source.body;
      
        try {
          const response = await axios.post(
            `https://${process.env.GATSBY_MYSHOPIFY_URL}/api/2025-07/graphql.json`,
            {
              query,
              variables,
            },
            {
              headers: {
                'X-Shopify-Storefront-Access-Token': process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
                'Content-Type': 'application/json',
              },
            }
          );
      
          if (response.data.errors) {
            return null;
          }
      
          const { cartLinesAdd } = response.data.data;
      
          if (cartLinesAdd.userErrors.length > 0) {
            return null;
          }

		  try {
			const addedItemIds = new Set((items || []).map((i) => i?.id).filter(Boolean))
			const addedLines = (cartLinesAdd?.cart?.lines?.edges || [])
				.map((e) => e?.node)
				.filter((n) => addedItemIds.has(n?.merchandise?.id))

			addedLines.forEach((addedLine) => {
				klaviyoTrack('Added to Cart', buildAddedToCartPayload({ addedLine, cart: cartLinesAdd.cart }))
			})
		  } catch {
			// ignore tracking errors
		  }
          
          //const cartContent = cartLinesAdd?.cart;
          //const isPromoPH = cartContent?.cost?.totalAmount?.amount >= 7000 ? true : false;
          //const cartLines = cartLinesAdd?.cart?.lines?.edges || [];
          
          /*
          const cartLines = cartLinesAdd?.cart?.lines?.edges || [];
      
          const isPromo = cartLines.some(line => {
            const metafieldValue = line.node.merchandise.product.metafield?.value ?? "";
            return metafieldValue === "island-life";
          });
        */
        
          setCheckout(cartLinesAdd.cart);
          setLoading(false);
          setDidJustAddToCart(true);
          setIsOpen(true);
          setTimeout(() => setDidJustAddToCart(false), 3000);
      
          return cartLinesAdd.cart;
        } catch {
          return null;
        }
    };
      

    const removeLineItem = async (lineItemID) => {
        setLoading(true)
        pendingQtyRef.current.delete(lineItemID)
        const cartId = checkout?.id;

        const variables = {
            cartId,
            lineIds: [lineItemID]
        };
          
        const query = RemoveLineItem.loc.source.body;
        
        try {
            const response = await axios.post(`https://${process.env.GATSBY_MYSHOPIFY_URL}/api/2025-07/graphql.json`, 
                {
                    query,
                    variables,
                }, 
                {
                    headers: {
                        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
                        'Content-Type': 'application/json',
                    },
                }
            );
        
            if (response.data.errors) {
                //console.error('GraphQL Errors:', response.data.errors);
                setLoading(false)
                return null;
            }

            const { cartLinesRemove } = response.data.data;

            if (cartLinesRemove.userErrors.length > 0) {
                //console.error('User Errors:', cartLinesRemove.userErrors);
                setLoading(false)
                return null;
            }
            
            /*
            const cartContent = cartLinesRemove?.cart;
            const isPromoPH = cartContent?.cost?.totalAmount?.amount >= 7000 ? true : false;

            
            const cartLines = cartLinesRemove?.cart?.lines?.edges;

            const isPromo = cartLines.some(line => {
                const metafieldValue = line.node.merchandise.product.metafield?.value ?? "";
                return metafieldValue === "butter-yellow";
            });
            */
            /*
                
            const cartLines = cartLinesRemove?.cart?.lines?.edges;

            //Bratz GWP
            const keepSafePromo = getCartPromoTotals(cartLines, "bratz");
            const keepSakesTotalOver = currencySelected.currencyCode !== "USD" && currencySelected.currencyCode !== "PHP" ? keepSafePromo?.total * conversionRate : keepSafePromo?.total;
            const bratzPromo = currencySelected.currencyCode !== "USD" && currencySelected.currencyCode !== "PHP" ? keepSafePromo?.promoTotal * conversionRate : keepSafePromo?.promoTotal;

            //Free bratz
            if(gwp && bratzPromo <= totalAmountCondition) {
                const freebieLine = cartLinesRemove?.cart?.lines?.edges?.find(
                    line => line.node.merchandise.id === freeBratzBox
                );

                if(freebieLine !== undefined) {
                    const remFreebie = await removeFreebie(freebieLine?.node?.id);

                    setCheckout(remFreebie.cart)
                    setLoading(false)
                    
                    return remFreebie.cart;
                }
            }

            //free sticker pack
            
            const TropicalStorem = getCartPromoTotals(cartLines, "tropical-glow");

            if(!TropicalStorem.hasPromo) {
                const freebieLine = cartLinesRemove?.cart?.lines?.edges?.find(
                    line => line.node.merchandise.id === freeTropicalGlow
                );

                if(freebieLine !== undefined) {
                    const remFreebie = await removeFreebie(freebieLine?.node?.id);

                    setCheckout(remFreebie.cart)
                    setLoading(false)
                    
                    return remFreebie.cart;
                }
            }
            */

            setCheckout(cartLinesRemove.cart)
            setLoading(false)
            
            return cartLinesRemove.cart;
        } catch {
            //console.error("Error creating cart:", error.response?.data || error.message);
            setLoading(false)
            return null;
        }
    }

    const updateLineItem = async (lineItemID, quantity) => {
        setLoading(true)
        const cartId = checkout?.id;

        const variables = {
            cartId,
            lines: [
              {
                id: lineItemID,
                quantity: quantity
              }
            ]
        };
          
        const query = UpdateLineItem.loc.source.body;
        
        try {
            const response = await axios.post(`https://${process.env.GATSBY_MYSHOPIFY_URL}/api/2025-07/graphql.json`, 
                {
                    query,
                    variables,
                }, 
                {
                    headers: {
                        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
                        'Content-Type': 'application/json',
                    },
                }
            );
        
            if (response.data.errors) {
                //console.error('GraphQL Errors:', response.data.errors);
                setLoading(false)
                return null;
            }

            const { cartLinesUpdate } = response.data.data;

            if (cartLinesUpdate.userErrors.length > 0) {
                //console.error('User Errors:', cartLinesUpdate.userErrors);
                setLoading(false)
                return null;
            }
            /*
            const cartLines = cartLinesUpdate?.cart?.lines?.edges;
     

            //keepsafe GWP
            const keepSafePromo = getCartPromoTotals(cartLines, "bratz");
            const keepSakesTotalOver = currencySelected.currencyCode !== "USD" && currencySelected.currencyCode !== "PHP" ? keepSafePromo?.total * conversionRate : keepSafePromo?.total;
            const bratzPromo = currencySelected.currencyCode !== "USD" && currencySelected.currencyCode !== "PHP" ? keepSafePromo?.promoTotal * conversionRate : keepSafePromo?.promoTotal;

            //free bratz
            if(gwp && bratzPromo >= totalAmountCondition && !cartLinesUpdate?.cart?.lines?.edges?.some(line => line.node.merchandise.id === freeBratzBox)) {
                const getFreebie = await addFreebie(freeBratzBox);

                if(getFreebie !== null) {
                    setCheckout(getFreebie.cart)
                    setLoading(false)
                    setDidJustAddToCart(true)
                    setIsOpen(true)
                    setTimeout(() => setDidJustAddToCart(false), 3000)

                    return getFreebie.cart;
                }
            }
            
            const TropicalStorem = getCartPromoTotals(cartLines, "tropical-glow");
            if(TropicalStorem.hasPromo && currencySelected?.currencyCode === "USD" && !cartLinesUpdate?.cart?.lines?.edges?.some(line => line.node.merchandise.id === freeTropicalGlow)) {
                const getFreebie = await addFreebie(freeTropicalGlow);

                if(getFreebie !== null) {
                    setCheckout(getFreebie.cart)
                    setLoading(false)
                    setDidJustAddToCart(true)
                    setIsOpen(true)
                    setTimeout(() => setDidJustAddToCart(false), 3000)

                    return getFreebie.cart;
                }
            }
            

            //free bratz GWP
            if(gwp  && bratzPromo <= totalAmountCondition) {
                const freebieLine = cartLinesUpdate?.cart?.lines?.edges?.find(
                    line => line.node.merchandise.id === freeBratzBox
                );

                if(freebieLine !== undefined) {
                    const remFreebie = await removeFreebie(freebieLine?.node?.id);

                    setCheckout(remFreebie.cart)
                    setLoading(false)
                    
                    return remFreebie.cart;
                }
            }

            //free sticker pack
            
            if(!TropicalStorem.hasPromo) {
                const freebieLine = cartLinesUpdate?.cart?.lines?.edges?.find(
                    line => line.node.merchandise.id === freeTropicalGlow
                );

                if(freebieLine !== undefined) {
                    const remFreebie = await removeFreebie(freebieLine?.node?.id);

                    setCheckout(remFreebie.cart)
                    setLoading(false)
                    
                    return remFreebie.cart;
                }
            }
            */

            setCheckout(cartLinesUpdate.cart)
            setLoading(false)

            return cartLinesUpdate.cart;
        } catch {
            //console.error("Error creating cart:", error.response?.data || error.message);
            setLoading(false)
            return null;
        }
    }

    const commitPendingQty = async () => {
        const entries = Array.from(pendingQtyRef.current.entries())
        pendingQtyRef.current.clear()
        for (const [lineId, quantity] of entries) {
            await updateLineItem(lineId, quantity)
        }
    }

    const flushLineItemUpdates = async () => {
        if (qtyTimerRef.current) {
            clearTimeout(qtyTimerRef.current)
            qtyTimerRef.current = null
        }
        const prev = commitPromiseRef.current
        const next = (async () => {
            if (prev) {
                try { await prev } catch { /* ignore */ }
            }
            await commitPendingQty()
        })()
        commitPromiseRef.current = next
        try {
            await next
        } finally {
            if (commitPromiseRef.current === next) commitPromiseRef.current = null
        }
    }

    const queueLineItemUpdate = (lineId, quantity) => {
        pendingQtyRef.current.set(lineId, quantity)
        if (qtyTimerRef.current) clearTimeout(qtyTimerRef.current)
        qtyTimerRef.current = setTimeout(() => { flushLineItemUpdates() }, 300)
    }

    const addFreebie = async (item) => {
        const cartId = checkout?.id;

        const variables = {
            cartId,
            lines: [
            {
                merchandiseId: item,
                quantity: 1
            }
            ]
        };
        const query = AddToCart.loc.source.body;
        
        try {
            const response = await axios.post(`https://${process.env.GATSBY_MYSHOPIFY_URL}/api/2025-07/graphql.json`, 
                {
                    query,
                    variables,
                }, 
                {
                    headers: {
                        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
                        'Content-Type': 'application/json',
                    },
                }
            );
        
            if (response.data.errors) {
                //console.error('GraphQL Errors:', response.data.errors);
                return null;
            }
        
            const { cartLinesAdd } = response.data.data;
        
            if (cartLinesAdd.userErrors.length > 0) {
                //console.error('User Errors:', cartLinesAdd.userErrors);
                return null;
            }
            
            return cartLinesAdd;
        } catch {
            //console.error("Error creating cart:", error.response?.data || error.message);
            return null;
        }
    }

    const removeFreebie = async (item) => {
        setLoading(true)
        const cartId = checkout?.id;

        const variables = {
            cartId,
            lineIds: [item]
        };
          
        const query = RemoveLineItem.loc.source.body;
        
        try {
            const response = await axios.post(`https://${process.env.GATSBY_MYSHOPIFY_URL}/api/2025-07/graphql.json`, 
                {
                    query,
                    variables,
                }, 
                {
                    headers: {
                        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
                        'Content-Type': 'application/json',
                    },
                }
            );
        
            if (response.data.errors) {
                //console.error('GraphQL Errors:', response.data.errors);
                return null;
            }
        
            const { cartLinesRemove } = response.data.data;
            
        
            if (cartLinesRemove.userErrors.length > 0) {
                //console.error('User Errors:', cartLinesRemove.userErrors);
                return null;
            }
            
            return cartLinesRemove;
        } catch {
            //console.error("Error creating cart:", error.response?.data || error.message);
            return null;
        }
    }

    React.useEffect(() => {
        if (typeof window !== 'undefined') {
            const cartIdValue = localStorage.getItem(localStorageKey);
            const sessionIdValue = localStorage.getItem(session_id);
            
            // Only proceed if we have valid cart data
            if (cartIdValue && cartIdValue !== 'null') {
                window.geMerchantSession = {
                    platformCartId: cartIdValue.split("key=")[1] || cartIdValue,
                    cartId: cartIdValue.split("key=")[1] || cartIdValue,
                    sessionId: sessionIdValue,
                };
            
                document.dispatchEvent(
                    new CustomEvent("geMerchantSession", {
                        bubbles: true,
                        detail: window.geMerchantSession,
                    })
                );
            }
        }

    }, [checkout]);

    return (
        <StoreContext.Provider
            value={{
                ...defaultValues,
                addVariantToCart,
                buyNow,
                removeLineItem,
                updateLineItem,
                queueLineItemUpdate,
                flushLineItemUpdates,
                checkout,
                loading,
                didJustAddToCart,
                isOpen,
                onOpen,
                onClose,
                setIsOpen,
                addVariantsToCartBulk,
                currencySelected,
			    setCurrencySelected,
            }}
        >
            {children}
        </StoreContext.Provider>
    )
}
